import { PageHeader, StatCard } from "@oms/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Package,
  ShoppingBag,
  Store,
  Truck,
} from "lucide-react";
import { PageShell } from "@/components/pages/page-shell";
import { ordersApi, integrationsApi } from "@/lib/api";
import { orderQueryKeys, ordersLiveQueryOptions } from "@/lib/orders";
import { DEFAULT_ORDERS_SEARCH } from "@/lib/orders-search";

export const Route = createFileRoute("/app/")({
  component: DashboardHomePage,
});

function DashboardHomePage() {
  const countsQuery = useQuery({
    queryKey: orderQueryKeys.counts(),
    queryFn: async () => {
      const response = await ordersApi.counts();
      return response.data ?? { all: 0 };
    },
    ...ordersLiveQueryOptions,
  });

  const recentOrdersQuery = useQuery({
    queryKey: orderQueryKeys.recent(),
    queryFn: async () => {
      const response = await ordersApi.list({ page: 1, pageSize: 4 });
      return response.data ?? [];
    },
    ...ordersLiveQueryOptions,
  });

  const connectionsQuery = useQuery({
    queryKey: ["integrations", "connections"],
    queryFn: async () => {
      const response = await integrationsApi.listConnections();
      return response.data ?? [];
    },
  });

  const counts = countsQuery.data ?? { all: 0 };
  const openOrders =
    (counts.pending ?? 0) +
    (counts.confirmed ?? 0) +
    (counts.processing ?? 0);
  const shippedToday = counts.shipped ?? 0;
  const recentOrders = recentOrdersQuery.data ?? [];
  const connections = connectionsQuery.data ?? [];

  return (
    <PageShell>
      <PageHeader
        title="Dashboard"
        description="Overview of orders, inventory, and fulfillment across all channels."
      />

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Open orders"
          value={openOrders}
          hint="Pending through processing"
          icon={<ShoppingBag className="h-5 w-5" />}
        />
        <StatCard
          label="Shipped today"
          value={shippedToday}
          hint="Awaiting delivery confirmation"
          icon={<Truck className="h-5 w-5" />}
        />
        <StatCard
          label="Low stock items"
          value="—"
          hint="Connect inventory sync"
          icon={<Package className="h-5 w-5" />}
        />
        <StatCard
          label="Connected stores"
          value={connections.length}
          hint={
            connections.length > 0
              ? `${connections.length} active connection${connections.length === 1 ? "" : "s"}`
              : "Connect your first store"
          }
          icon={<Store className="h-5 w-5" />}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-sm lg:col-span-2">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-base font-semibold text-text">
                Recent activity
              </h2>
              <p className="text-sm text-text-muted">
                Latest orders synced from your channels.
              </p>
            </div>
            <Link
              to="/app/orders"
              search={DEFAULT_ORDERS_SEARCH}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              View all
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <ul className="mt-6 divide-y divide-border-muted">
            {recentOrders.map((order) => (
              <li
                key={order.id}
                className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 space-y-1">
                  <p className="truncate text-sm font-medium text-text">
                    {order.externalId}
                  </p>
                  <p className="truncate text-xs text-text-muted">
                    {order.customerName} · {order.channel}
                  </p>
                </div>
                <span className="shrink-0 text-xs capitalize text-text-subtle">
                  {order.status}
                </span>
              </li>
            ))}
            {recentOrders.length === 0 ? (
              <li className="py-4 text-sm text-text-muted">
                No orders synced yet. Connect a store to start importing orders.
              </li>
            ) : null}
          </ul>
        </div>

        <div className="rounded-2xl border border-dashed border-brand-200 bg-brand-50/50 p-6">
          <h2 className="text-base font-semibold text-text">
            Connect a store
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-text-secondary">
            Orders flow in automatically from Meesho, Amazon, Flipkart, and
            other integrations. No manual order entry needed.
          </p>
          <Link
            to="/app/integrations"
            className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Connect a store
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </PageShell>
  );
}
